import { BrowserRouter, Link, NavLink, Route, Routes } from 'react-router'
import { AuthProvider, RequireAuth, atLeast, useAuth } from './auth.js'
import { TimerProvider, TimersMenu } from './timer.js'
import { Breadcrumbs } from './breadcrumbs.js'
import { NotificationsBell } from './notifications.js'
import { CommandPalette } from './palette.js'
import { TooltipLayer } from './tooltip.js'
import { useRealtime } from './realtime.js'
import { onQueueChange, startOfflineSync } from './offline.js'
import { currentLocale, setLocale, subscribeLocale, t } from './i18n.js'
import { TimesheetsPage } from './pages/Timesheets.js'
import { ProjectApprovalsPage } from './pages/ProjectApprovals.js'
import { ProjectRatesPage } from './pages/Rates.js'
import { LeavePage } from './pages/Leave.js'
import { PayrollPage } from './pages/Payroll.js'
import { ImportPage } from './pages/Import.js'
import { ReportsPage } from './pages/Reports.js'
import { AnalyticsPage } from './pages/Analytics.js'
import { LoginPage } from './pages/Login.js'
import { AcceptInvitePage } from './pages/AcceptInvite.js'
import { ConfirmEmailPage } from './pages/ConfirmEmail.js'
import { HomePage } from './pages/Home.js'
import { AccountPage } from './pages/Account.js'
import { PeoplePage } from './pages/People.js'
import { NewProjectPage, ProjectsPage } from './pages/Projects.js'
import { ProjectDetailPage } from './pages/ProjectDetail.js'
import { PortfoliosPage } from './pages/Portfolios.js'
import { PortfolioDetailPage } from './pages/PortfolioDetail.js'
import { PortfolioBoardPage } from './pages/PortfolioBoard.js'
import { PortfolioGanttPage } from './pages/PortfolioGantt.js'
import { PortfolioListPage } from './pages/PortfolioList.js'
import { BoardPage } from './pages/Board.js'
import { GanttPage } from './pages/Gantt.js'
import { ListPage } from './pages/List.js'
import { CalendarPage } from './pages/Calendar.js'
import { SearchPage } from './pages/Search.js'
import { useLocation, useNavigate } from 'react-router'
import { useState, useEffect, useRef, useSyncExternalStore, type FormEvent } from 'react'
import { Menu as MenuIcon, X as XIcon } from 'lucide-react'
import { TimePage } from './pages/Time.js'
import { AdminPage } from './pages/Admin.js'

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
          <Route path="/*" element={<RequireAuth><TimerProvider><Shell /></TimerProvider></RequireAuth>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}

/** Management: the shared workflows every person uses (their own submissions, leave, reports); Payroll for managers+. */
function ManagementMenu() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const loc = useLocation()
  const active = ['/timesheets', '/leave', '/reports', '/payroll'].some((p) => loc.pathname.startsWith(p))
  useEffect(() => { setOpen(false) }, [loc.pathname])
  return (
    <div className="menu" onMouseLeave={() => setOpen(false)}>
      <button className={`link plain menubtn ${active ? 'active' : ''}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-tip={t("Timesheets, leave, reports and payroll")}>Management</button>
      {open && (
        <div className="menupop" role="menu">
          <NavLink to="/timesheets" role="menuitem" data-tip={t("Submit your project-weeks for approval; see decisions")}>{t('nav.timesheets')}</NavLink>
          <NavLink to="/leave" role="menuitem" data-tip={t("Request time off; managers approve their reports here")}>{t('nav.leave')}</NavLink>
          <NavLink to="/reports" role="menuitem" data-tip={t("Hours (and money for managers) grouped and filtered; CSV / Excel / PDF; schedules")}>{t('nav.reports')}</NavLink>
          {atLeast(user, 'manager') && <NavLink to="/payroll" role="menuitem" data-tip={t("Period close: who is approved and ready; export CSV")}>{t('nav.payroll')}</NavLink>}
        </div>
      )}
    </div>
  )
}

function SearchBox() {
  const nav = useNavigate()
  const [q, setQ] = useState('')
  const submit = (e: FormEvent) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`) }
  return <form onSubmit={submit} className="searchbox"><input type="search" placeholder={t("Search tasks, projects, people…")} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("Search")} data-tip={t("Search task keys (DEN-12), titles, projects and people; Enter for the results page")} /></form>
}

function Shell() {
  const { user, mfa } = useAuth()
  useRealtime(!!user)
  const [queued, setQueued] = useState(0)
  const [offlineMsg, setOfflineMsg] = useState<string | null>(null)
  useEffect(() => { setLocale(user?.locale) }, [user?.locale])
  const locale = useSyncExternalStore(subscribeLocale, currentLocale)
  // Publish the sticky header height so tab strips can stick right under it.
  const stickyRef = useRef<HTMLDivElement>(null)
  // Small screens: the primary links live in a drawer under the header.
  const [navOpen, setNavOpen] = useState(false)
  const loc = useLocation()
  useEffect(() => { setNavOpen(false) }, [loc.pathname, loc.search])
  useEffect(() => {
    const el = stickyRef.current; if (!el) return
    const set = () => document.documentElement.style.setProperty('--stickyh', `${el.offsetHeight}px`)
    set(); const ro = new ResizeObserver(set); ro.observe(el); return () => ro.disconnect()
  }, [])
  useEffect(() => { const off1 = onQueueChange(setQueued); const off2 = startOfflineSync(setOfflineMsg); return () => { off1(); off2() } }, [])
  return (
    <div className="layout" key={locale} lang={locale}>
      {queued > 0 && <div className="banner">{t(queued === 1 ? 'offline.queued' : 'offline.queuedPlural', { n: queued })}</div>}
      {offlineMsg && <div className="banner">{offlineMsg} <button className="link" onClick={() => setOfflineMsg(null)}>ok</button></div>}
      {mfa?.required && (
        <div className="banner">{t("Your role requires two-factor authentication for password sign-in.")} <Link to="/account">{t("Set it up now")}</Link>.</div>
      )}
      <TooltipLayer />
      <CommandPalette />
      <div className="stickytop" ref={stickyRef}>
      <nav className="topbar">
        <Link to="/" className="brand" data-tip={t("Home dashboard")}>ADKOM PM <span className="build">{import.meta.env.VITE_APP_VERSION} build {import.meta.env.VITE_APP_BUILD}</span></Link>
        <div className="desknav">
          <NavLink to="/" end data-tip={t("Your week, tasks and projects at a glance")}>{t('nav.home')}</NavLink>
          <NavLink to="/time" data-tip={t("Log hours, run timers, add corrections")}>{t('nav.time')}</NavLink>
          <NavLink to="/projects" data-tip={t("Projects you can see; boards, Gantt, lists")}>{t('nav.projects')}</NavLink>
          <NavLink to="/portfolios" data-tip={t("Groups of projects with a shared board, list and Gantt")}>{t('nav.portfolios')}</NavLink>
          <NavLink to="/people" data-tip={t("Directory, invites, roles")}>{t('nav.people')}</NavLink>
          <ManagementMenu />
          {atLeast(user, 'admin') && <NavLink to="/admin" data-tip={t("Audit log, sign-in attempts, flags, retention, import")}>{t('nav.admin')}</NavLink>}
        </div>
        <span className="spacer" />
        <TimersMenu />
        <button className="button cmdk" data-tip={`Open the command palette (${/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl-K'}): jump to any page, project or task, run actions`} onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}>Command Palette</button>
        <SearchBox />
        <NotificationsBell />
        <NavLink to="/account" className="who" data-tip={t("Your account: profile, password, security, calendar feed, sign out")}>
          {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="avatar" /> : null}
          <span className="whoname">{user?.displayName}</span>
        </NavLink>
        <button className="navtoggle" aria-label={navOpen ? t("Close menu") : t("Open menu")} aria-expanded={navOpen} aria-controls="mobile-nav" onClick={() => setNavOpen((o) => !o)}>{navOpen ? <XIcon size={22} aria-hidden /> : <MenuIcon size={22} aria-hidden />}</button>
      </nav>
      {navOpen && (
        <nav className="drawer" id="mobile-nav" aria-label={t("Main menu")}>
          <SearchBox />
          <NavLink to="/" end>{t('nav.home')}</NavLink>
          <NavLink to="/time">{t('nav.time')}</NavLink>
          <NavLink to="/projects">{t('nav.projects')}</NavLink>
          <NavLink to="/portfolios">{t('nav.portfolios')}</NavLink>
          <NavLink to="/people">{t('nav.people')}</NavLink>
          <div className="drawergroup">{t("Management")}</div>
          <NavLink to="/timesheets">{t('nav.timesheets')}</NavLink>
          <NavLink to="/leave">{t('nav.leave')}</NavLink>
          <NavLink to="/reports">{t('nav.reports')}</NavLink>
          {atLeast(user, 'manager') && <NavLink to="/payroll">{t('nav.payroll')}</NavLink>}
          {atLeast(user, 'admin') && <NavLink to="/admin">{t('nav.admin')}</NavLink>}
          <NavLink to="/account">{t("Account")}</NavLink>
        </nav>
      )}
      <Breadcrumbs />
      </div>
      <main className="shell">
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="people" element={<PeoplePage />} />
          <Route path="time" element={<TimePage />} />
          <Route path="timesheets" element={<TimesheetsPage />} />
          <Route path="leave" element={<LeavePage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="payroll" element={<RequireAuth role="manager"><PayrollPage /></RequireAuth>} />
          <Route path="projects/:projectId/approvals" element={<ProjectApprovalsPage />} />
          <Route path="projects/:projectId/rates" element={<ProjectRatesPage />} />
          <Route path="projects/:projectId/analytics" element={<AnalyticsPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/new" element={<NewProjectPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="projects/:projectId/board" element={<BoardPage />} />
          <Route path="projects/:projectId/gantt" element={<GanttPage />} />
          <Route path="projects/:projectId/list" element={<ListPage />} />
          <Route path="projects/:projectId/calendar" element={<CalendarPage />} />
          <Route path="portfolios" element={<PortfoliosPage />} />
          <Route path="portfolios/:portfolioId" element={<PortfolioDetailPage />} />
          <Route path="portfolios/:portfolioId/board" element={<PortfolioBoardPage />} />
          <Route path="portfolios/:portfolioId/gantt" element={<PortfolioGanttPage />} />
          <Route path="portfolios/:portfolioId/list" element={<PortfolioListPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="admin" element={<RequireAuth role="admin"><AdminPage /></RequireAuth>} />
          <Route path="admin/import" element={<RequireAuth role="admin"><ImportPage /></RequireAuth>} />
          <Route path="*" element={<p className="muted">{t("Not found.")}</p>} />
        </Routes>
      </main>
    </div>
  )
}
